import React, { useEffect, useMemo, useState } from 'react';
import { PencilLine, Sparkle } from 'lucide-react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

export interface ProjectOverviewProps {
  title?: string;
  projectDescription?: string;
  isLoading?: boolean;
  hasError?: boolean;
  onRetry?: () => void;
  bodyHtml?: string;
  /** Called when the user saves; should persist to Salesforce. */
  onSave?: (html: string) => Promise<void>;
  saveCommitId?: number;
}

export default function ProjectOverview({
  title = 'Project Overview',
  projectDescription,
  isLoading,
  hasError,
  onRetry,
  bodyHtml,
  onSave,
  saveCommitId,
}: ProjectOverviewProps) {
  const projectDescriptionHtml = useMemo(() => {
    const raw = projectDescription?.trim();
    if (!raw) return undefined;

    const escapeHtml = (s: string) =>
      s
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');

    const blocks = raw
      .replace(/<br\s*\/?>/gi, '\n')
      .split(/\r?\n\s*\r?\n/)
      .map((b) => b.trim())
      .filter((b) => !!b);

    if (blocks.length === 0) return undefined;

    return blocks.map((b) => `<p>${escapeHtml(b).replace(/\r?\n/g, '<br />')}</p>`).join('');
  }, [projectDescription]);

  const initialHtml = useMemo(() => {
    if (bodyHtml) return bodyHtml;
    if (projectDescriptionHtml) return projectDescriptionHtml;
    return '<p>No Project Description Provided</p>';
  }, [bodyHtml, projectDescriptionHtml]);

  const [isEditing, setIsEditing] = useState(false);
  const [editorHtml, setEditorHtml] = useState<string>('');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  useEffect(() => {
    setIsEditing(false);
    setEditorHtml('');
    setSaveError(null);
  }, [saveCommitId]);

  const startEditing = () => {
    setEditorHtml(initialHtml);
    setSaveError(null);
    setIsEditing(true);
  };

  const cancelEditing = () => {
    setIsEditing(false);
    setEditorHtml('');
    setSaveError(null);
  };

  const handleSave = async () => {
    if (!onSave) return;
    setSaving(true);
    setSaveError(null);
    try {
      await onSave(editorHtml);
      setIsEditing(false);
      setEditorHtml('');
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={`w-full rounded-[8px] pl-[10px] pr-[45px] py-[20px] mb-[20px] ${hasError ? 'bg-red-50' : 'bg-[#53BEE81A]'}`}>
      <div className="flex items-start gap-[12px]">
        <div className="mt-[2px] flex size-[24px] items-center justify-center">
          <div className="flex-none rotate-[13.98deg]">
            <Sparkle size={20} color={hasError ? '#ef4444' : '#25ADE3'} />
          </div>
        </div>

        <div className="flex-1">
          <div className="flex items-center justify-between">
            <p className="onepager-section-title text-[20px] leading-[24px]">{title}</p>

            {!isEditing && !hasError && !isLoading && onSave && (
              <button
                type="button"
                className="inline-flex items-center gap-[6px] text-center font-['Inter',sans-serif] text-[14px] font-medium leading-[20px] tracking-[-0.1504px] text-[#53BEE8]"
                onClick={startEditing}
                data-pdf-exclude
              >
                <PencilLine size={16} color="#53BEE8" />
                Edit
              </button>
            )}

            {isEditing && (
              <div className="flex items-center gap-2" data-pdf-exclude>
                <button
                  type="button"
                  className="px-3 py-1 rounded bg-[#29446D] text-white text-sm font-medium disabled:opacity-50"
                  onClick={handleSave}
                  disabled={saving}
                >
                  {saving ? 'Saving…' : 'Save'}
                </button>
                <button
                  type="button"
                  className="px-3 py-1 rounded border border-gray-200 text-[#29446D] text-sm font-medium"
                  onClick={cancelEditing}
                  disabled={saving}
                >
                  Cancel
                </button>
              </div>
            )}
          </div>

          {!isEditing && hasError && (
            <p className="mt-[9px] text-[16px] text-red-600">
              Generation Failed.{' '}
              <button type="button" className="underline" onClick={onRetry}>
                Try Again?
              </button>
            </p>
          )}

          {!isEditing && !hasError && isLoading && (
            <div className="mt-[9px] space-y-2">
              <div className="animate-pulse rounded bg-[#25ADE3]/20 h-[18px] w-full" />
              <div className="animate-pulse rounded bg-[#25ADE3]/20 h-[18px] w-[92%]" />
              <div className="animate-pulse rounded bg-[#25ADE3]/20 h-[18px] w-[80%]" />
            </div>
          )}

          {!isEditing && !hasError && !isLoading && (
            <div
              className="mt-[9px] ql-snow font-['Open_Sans',sans-serif] text-[16px] font-normal leading-[22.8px] text-[#31394C] text-left"
              style={{ fontVariationSettings: "'wdth' 100" }}
              dangerouslySetInnerHTML={{ __html: `<div class="ql-editor">${initialHtml}</div>` }}
            />
          )}

          {isEditing && (
            <div className="mt-[9px] space-y-2" data-pdf-exclude>
              {saveError && (
                <p className="text-sm text-red-600">{saveError}</p>
              )}
              <div className="snapshot-quill bg-gray-50 rounded-lg border border-gray-200 overflow-hidden">
                <ReactQuill
                  theme="snow"
                  value={editorHtml}
                  onChange={setEditorHtml}
                  modules={{
                    toolbar: [
                      [{ header: [1, 2, false] }],
                      ['bold', 'italic', 'underline'],
                      [{ list: 'ordered' }, { list: 'bullet' }],
                      ['link'],
                      ['clean'],
                    ],
                  }}
                />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
